import React, { useMemo, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Popover, Popconfirm } from 'antd';
import Icon, { HolderOutlined, LoadingOutlined, SettingOutlined, CloseOutlined, DeleteOutlined, CopyOutlined, DownloadOutlined, QuestionCircleOutlined, RightOutlined, DownOutlined } from '@ant-design/icons';
import { GlobalVariable, parsePath } from '@kemu-io/kemu-core/common/managers/globalVariables.js';
import { Spin } from 'antd';
import StandardUITooltip from '@components/common/StandardUITooltip/StandardUITooltip';
import { ReactComponent as ReplayIcon } from '@src/assets/img/dataTypes/replay-icon.svg';
import styles from '../GlobalVariablesPanel.module.css';
import { DataType } from '@kemu-io/hs-types';
import useTranslation from '@hooks/useTranslation';
import ImportExportMenu from '../ImportExportMenu';

type Props = {
  entry: GlobalVariable;
  loading: boolean;
  editingEntry: string | null;
  editingValue: string;
  dispatchingVariableChange: Record<string, boolean>;
  handleNameDoubleClick: (entry: GlobalVariable) => void;
  handleInputChange: (value: string) => void;
  handleSaveEdit: (entry: GlobalVariable) => void;
  handleKeyPress: (event: React.KeyboardEvent, entry: GlobalVariable) => void;
  handleClearValue: (entry: GlobalVariable) => void;
  handleCopy: (entry: GlobalVariable) => void;
  handleConfigureVariable: (entry: GlobalVariable) => void;
  handleDeleteVariable: (entry: GlobalVariable) => void;
  handlePushLocalValue: (entry: GlobalVariable) => void;
  handleDownloadBinaryFile: (entry: GlobalVariable) => void;
  renderControl: (entry: GlobalVariable) => React.ReactNode;
  depth?: number;
  isGroup?: boolean;
  isExpanded?: boolean;
  hasChildren?: boolean;
  onToggleGroup?: () => void;
  isOver?: boolean;
  showIndicatorAbove?: boolean;
  projectedDepth?: number;
  isNesting?: boolean;
  clone?: boolean;
  childCount?: number;
  onGroupExport?: (groupName: string) => void;
  onGroupCopy?: (groupName: string) => void;
  onGroupPaste?: (groupName: string) => void;
  onGroupImport?: (groupName: string, file: File) => void;
  canGroupPaste?: boolean;
};

const SortableVariableRow = (props: Props) => {
  const {
    entry,
    loading,
    editingEntry,
    editingValue,
    handleNameDoubleClick,
    handleInputChange,
    handleSaveEdit,
    handleKeyPress,
    handleClearValue,
    handleCopy,
    handleConfigureVariable,
    handleDeleteVariable,
    handlePushLocalValue,
    handleDownloadBinaryFile,
    renderControl,
    depth = 0,
    isGroup = false,
    isExpanded = false,
    hasChildren = false,
    onToggleGroup,
    isOver: isOverProp,
    showIndicatorAbove = false,
    projectedDepth,
    isNesting = false,
    clone = false,
    childCount,
    onGroupExport,
    onGroupCopy,
    onGroupPaste,
    onGroupImport,
    canGroupPaste = false,
  } = props;

  const groupFileInputRef = useRef<HTMLInputElement>(null);

  const t = useTranslation('GlobalVariablesPanel');
  
  // Don't use useSortable when in clone mode (for drag overlay)
  // Always call the hook, but conditionally use its results
  const sortableResult = useSortable({ id: entry.name, disabled: clone });
  
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    isOver: isOverFromSortable,
  } = clone ? {
    attributes: {},
    listeners: {},
    setNodeRef: () => {},
    setActivatorNodeRef: () => {},
    transform: null,
    transition: undefined,
    isDragging: false,
    isOver: false,
  } : sortableResult;

  const displayDepth = projectedDepth !== undefined ? projectedDepth : depth;
  const isOver = isOverProp !== undefined ? isOverProp : isOverFromSortable;
  
  // Calculate left offset for indicator
  // When nesting into a group: indent to show it's going inside (drag handle + depth indentation)
  // When reordering: start from left edge (0px) to span full width
  // Drag handle: icon (~14px) + padding (4px each side = 8px) = ~22px, plus 8px margin = ~30px
  const DRAG_HANDLE_OFFSET = 30;
  
  let indicatorLeft = 0; // Default: start from left edge
  if (isNesting && isOver && projectedDepth !== undefined) {
    // When nesting, indent by drag handle offset + projected depth
    indicatorLeft = DRAG_HANDLE_OFFSET + (projectedDepth * 20);
  }

  // Don't apply transform to items during drag - items should stay in place
  // Only the dragged item (shown in DragOverlay) moves, other items remain static
  // This prevents items from shifting position and matches the dnd-kit example behavior
  const style = {
    transform: undefined, // Never apply transform - items stay in place
    transition: undefined, // No transitions during drag
    opacity: isDragging ? 0.5 : clone ? 0.8 : 1,
  };

  /**
   * Converts ImageData to a data URL for preview
   * @returns Data URL string or null if invalid
   */
  const imageDataToDataURL = useMemo(() => {
    if (!entry.lastValue) {
      return null;
    }

    // Check if it's ImageData type (either by lastDataType or by instance check)
    const isImageData = entry.lastDataType === DataType.ImageData || 
                       (entry.controlType === 'image' && entry.lastValue instanceof ImageData);
    
    if (!isImageData) {
      return null;
    }

    const imageData = entry.lastValue as ImageData;
    if (!imageData || !imageData.width || !imageData.height) {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL('image/png');
  }, [entry.lastValue, entry.lastDataType, entry.controlType]);

  /**
   * Checks if this entry should show an image preview tooltip
   */
  const shouldShowImagePreview = useMemo(() => {
    const isImageType = entry.controlType === 'image' || 
                       (entry.controlType === 'anything' && entry.lastDataType === DataType.ImageData);
    return isImageType && imageDataToDataURL !== null;
  }, [entry.controlType, entry.lastDataType, imageDataToDataURL]);

  /**
   * Renders the image preview popover content
   */
  const imagePreviewContent = useMemo(() => {
    if (!imageDataToDataURL) {
      return null;
    }

    // Limit preview size to a reasonable maximum
    const maxPreviewSize = 400;
    const imageData = entry.lastValue as ImageData;
    const aspectRatio = imageData.width / imageData.height;
    let previewWidth = imageData.width;
    let previewHeight = imageData.height;

    if (previewWidth > maxPreviewSize) {
      previewWidth = maxPreviewSize;
      previewHeight = maxPreviewSize / aspectRatio;
    }
    if (previewHeight > maxPreviewSize) {
      previewHeight = maxPreviewSize;
      previewWidth = maxPreviewSize * aspectRatio;
    }

    return (
      <img
        src={imageDataToDataURL}
        alt="Image preview"
        style={{
          maxWidth: `${previewWidth}px`,
          maxHeight: `${previewHeight}px`,
          display: 'block',
        }}
      />
    );
  }, [imageDataToDataURL, entry.lastValue]);

  // Extract display name from full path
  const { name: displayName } = parsePath(entry.name);
  
  const EntryField = (
    <div
      className={styles.ClickableVariableName}
      onDoubleClick={() => handleNameDoubleClick(entry)}
    >
      <div className={styles.VariableName}>
        {entry.helpText && (
          <span className={styles.HelpIconWrapper}>
            <StandardUITooltip title={entry.helpText} placement="top">
              <QuestionCircleOutlined />
            </StandardUITooltip>
          </span>
        )}
        <span className={styles.VariableNameText} title={entry.name} >
          {displayName}
        </span>
      </div>
    </div>
  );

  return (
    <div
      ref={clone ? undefined : setNodeRef}
      style={style}
      className={`${styles.TableRow} ${isDragging ? styles.Dragging : ''} ${displayDepth > 0 ? styles.NestedVariable : ''} ${clone ? styles.Clone : ''}`}
    >
      {isOver && !clone && (
        <div 
          className={styles.DropIndicator}
          style={{
            ...(showIndicatorAbove ? { top: '-1px' } : { bottom: '-1px' }),
            left: `${indicatorLeft}px`, // Indent based on drag handle + projected depth
            right: 0, // Span to right edge
          }}
        />
      )}
      <div className={`${styles.TableCell} ${styles.nameColumn}`}>
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', paddingLeft: `${displayDepth * 20}px` }}>
          {!clone && (
            <div className={styles.DragHandle} ref={setActivatorNodeRef} {...listeners} {...attributes}>
              <HolderOutlined />
            </div>
          )}
          {clone && (
            <div className={styles.DragHandle}>
              <HolderOutlined />
            </div>
          )}
          <div className={styles.NameContent} style={{ flex: 1, minWidth: 0 }}>
            {isGroup && hasChildren && (
              <Button
                type="text"
                icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleGroup?.();
                }}
                className={styles.GroupToggle}
                size="small"
              />
            )}
            {editingEntry === entry.name ? (
              <input
                type="text"
                value={editingValue}
                onChange={(e) => handleInputChange(e.target.value.replace(/-/g, '_'))}
                onBlur={() => handleSaveEdit(entry)}
                onKeyDown={(e) => handleKeyPress(e, entry)}
                className={styles.EditableInput}
                autoFocus
              />
            ) : (
              EntryField
            )}
          </div>
          {loading ? (
            <Spin style={{ color: 'white', marginLeft: 'auto', flexShrink: 0 }} spinning={true} indicator={<LoadingOutlined />} />
          ) : (
            <div className={styles.ActionButtons} style={{ marginLeft: 'auto', flexShrink: 0 }}>
              {entry.lastValue !== null && entry.controlType !== 'button' && (
                <StandardUITooltip title={t('EmitLocalValue')} placement="top">
                  <Button
                    type="text"
                    icon={<Icon component={ReplayIcon} />}
                    onClick={() => handlePushLocalValue(entry)}
                    className={styles.ActionButton}
                  />
                </StandardUITooltip>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={`${styles.TableCell} ${styles.valueColumn}`}>
        <div className={styles.ValueContainer}>
          {shouldShowImagePreview ? (
            <Popover
              content={imagePreviewContent}
              trigger="hover"
              placement="right"
              overlayClassName={styles.ImagePreviewPopover}
              mouseEnterDelay={0.3}
            >
              <div style={{ display: 'inline-block', width: '100%' }}>
                {renderControl(entry)}
              </div>
            </Popover>
          ) : (
            renderControl(entry)
          )}
          {!isGroup && (
            <div className={styles.ActionButtons}>
            {(entry.controlType === 'image' ||
              (entry.controlType === 'anything' && entry.lastDataType === DataType.ImageData)) &&
              entry.lastValue && (
                <StandardUITooltip title={t.withBaseKey('CommonWords')('Copy')}>
                  <Button
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={() => handleCopy(entry)}
                    className={styles.ActionButton}
                  />
                </StandardUITooltip>
              )}
            {(entry.controlType === 'binaryFile' ||
              entry.controlType === 'image' ||
              (entry.controlType === 'anything' &&
                (entry.lastDataType === DataType.BinaryFile || entry.lastDataType === DataType.ImageData))) &&
              entry.lastValue && (
                <StandardUITooltip title={t.withBaseKey('CommonWords')('Download')}>
                  <Button
                    type="text"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadBinaryFile(entry)}
                    className={styles.ActionButton}
                  />
                </StandardUITooltip>
              )}
            <StandardUITooltip title={t('Configure')} placement="top">
              <Button
                type="text"
                icon={<SettingOutlined />}
                onClick={() => handleConfigureVariable(entry)}
                className={styles.ActionButton}
              />
            </StandardUITooltip>
            <StandardUITooltip title={t('Clear')} placement="top">
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => handleClearValue(entry)}
                className={styles.ActionButton}
              />
            </StandardUITooltip>
            <StandardUITooltip title={t('Delete')} placement="top">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteVariable(entry)}
                className={styles.ActionButton}
              />
            </StandardUITooltip>
            </div>
          )}
          {isGroup && (
            <div className={styles.ActionButtons}>
              {onGroupExport && onGroupCopy && onGroupPaste && onGroupImport && (
                <ImportExportMenu
                  onDownload={() => onGroupExport(entry.name)}
                  onCopy={() => onGroupCopy(entry.name)}
                  onPaste={() => onGroupPaste(entry.name)}
                  onImport={() => {
                    groupFileInputRef.current?.click();
                  }}
                  canPaste={canGroupPaste}
                />
              )}
              <StandardUITooltip title={t('Configure')} placement="top">
                <Button
                  type="text"
                  icon={<SettingOutlined />}
                  onClick={() => handleConfigureVariable(entry)}
                  className={styles.ActionButton}
                />
              </StandardUITooltip>
              <Popconfirm
                title={t('DeleteGroupConfirm.Title')}
                description={t('DeleteGroupConfirm.Description')}
                onConfirm={() => handleDeleteVariable(entry)}
                okText={t.withBaseKey('CommonWords')('Delete')}
                cancelText={t.withBaseKey('CommonWords')('Cancel')}
                okButtonProps={{ danger: true }}
              >
                <StandardUITooltip title={t('Delete')} placement="top">
                  <span>
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      className={styles.ActionButton}
                    />
                  </span>
                </StandardUITooltip>
              </Popconfirm>
              {onGroupImport && (
                <input
                  ref={groupFileInputRef}
                  type="file"
                  accept=".json"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file && onGroupImport) {
                      onGroupImport(entry.name, file);
                    }
                    if (groupFileInputRef.current) {
                      groupFileInputRef.current.value = '';
                    }
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SortableVariableRow;
